import { communicationService, eventsRepository } from '@ad-engine/communication';
import {
	AdSlot,
	AdSlotEvent,
	context,
	DEFAULT_MAX_DELAY,
	Dictionary,
	FandomTaxonomy,
	pbjsFactory,
	targetingService,
	Tcf,
	tcf,
	utils,
} from '@ad-engine/core';
import {
	BIDDERS_AUCTION_DONE,
	BIDDERS_BIDS_CALLED,
	SLOT_REFRESHER_SET_MAXIMUM_SLOT_HEIGHT,
} from '../../communication/events/events-bidders';
import { defaultSlotBidGroup, getSlotAliasOrName } from '../bidder-helper';
import { BidderConfig, BidderProvider, BidsRefreshing } from '../bidder-provider';
import { adaptersRegistry } from './adapters-registry';
import { dsaHandler } from './dsa/handler';
import { id5 } from './id5';
import { connectedId } from './liveintent-connected-id';
import { prebidDataTracking } from './prebid-data-tracking';
import { requestBids } from './prebid-helper';
import { getSettings } from './prebid-settings';
import { roundBucketCpm } from './price-helper';
import { prebidIdRetriever } from './utils/id-retriever';
import { yahooConnectId } from './yahoo-connect-id';
import { uid2 } from './uid2';
import { aps } from './aps';

const logGroup = 'prebid';

const displayGranularity = {
	buckets: [
		{
			max: 3,
			increment: 0.01,
			min: 0,
		},
		{
			max: 8,
			increment: 0.05,
		},
		{
			max: 20,
			increment: 0.5,
		},
		{
			max: 46,
			increment: 1,
		},
	],
};

const videoGranularity = {
	buckets: [
		{
			max: 10,
			increment: 0.01,
		},
		{
			max: 20,
			increment: 0.5,
		},
		{
			max: 50,
			increment: 1,
		},
	],
};

const s2sRubiconAccountId = 7450;

interface PrebidConfig extends BidderConfig {
	[bidderName: string]: { enabled: boolean; slots: Dictionary } | boolean;
}

export interface UserIdConfig {
	name: string;
	params: object;
	storage: object;
}

communicationService.onSlotEvent(AdSlotEvent.VIDEO_AD_IMPRESSION, ({ slot }) =>
	markWinningVideoBidAsUsed(slot),
);
communicationService.onSlotEvent(AdSlotEvent.VIDEO_AD_ERROR, ({ slot }) =>
	markWinningVideoBidAsUsed(slot),
);

async function markWinningVideoBidAsUsed(adSlot: AdSlot): Promise<void> {
	// Mark ad as rendered
	const adId: string = targetingService.get('hb_adid', adSlot.getSlotName());

	if (adId) {
		const pbjs: Pbjs = await pbjsFactory.init();

		utils.logger(logGroup, 'marking video bid as used', adSlot.getSlotName(), adId);
		pbjs.markWinningBidAsUsed({ adId });
		adSlot.emit(AdSlotEvent.VIDEO_AD_USED);
	}
}

export class PrebidProvider extends BidderProvider {
	adUnits: PrebidAdUnit[];
	bidsRefreshing: BidsRefreshing;
	prebidConfig: Dictionary;
	tcf: Tcf = tcf;

	constructor(
		public bidderConfig: PrebidConfig,
		public timeout = DEFAULT_MAX_DELAY,
		private bidGroup: string = defaultSlotBidGroup,
	) {
		super('prebid', bidderConfig, timeout);
		adaptersRegistry.configureAdapters();

		communicationService.on(
			eventsRepository.MOBILE_MORE_ICB_EXPERIMENT,
			() => {
				adaptersRegistry.getAdapters().forEach((adapter) => {
					context.remove(`bidders.prebid.${adapter.bidderName}.slots.mobile_top_leaderboard`);
				});
			},
			false,
		);

		this.adUnits = adaptersRegistry.setupAdUnits(this.bidGroup);
		this.bidsRefreshing = context.get('bidders.prebid.bidsRefreshing') || {};

		this.prebidConfig = {
			enableTIDs: true,
			bidderSequence: 'random',
			bidderTimeout: this.timeout,
			cache: {
				url: 'https://prebid.adnxs.com/pbc/v1/cache',
			},
			debug: ['1', 'true'].includes(utils.queryString.get('pbjs_debug')),
			cpmRoundingFunction: roundBucketCpm,
			mediaTypePriceGranularity: {
				banner: displayGranularity,
				video: videoGranularity,
				'video-outstream': videoGranularity,
			},
			priceGranularity: 'dense',
			ozone: {
				enhancedAdserverTargeting: false,
				oz_whitelist_adserver_keys: [],
			},
			rubicon: {
				singleRequest: true,
			},
			userSync: {
				filterSettings: {
					iframe: {
						bidders: '*',
						filter: 'include',
					},
					image: {
						bidders: '*',
						filter: 'include',
					},
				},
				userIds: [],
				auctionDelay: context.get('bidders.prebid.auctionDelay') ?? 50,
				syncsPerBidder: 3,
				syncDelay: 6000,
			},
		};

		if (utils.isCoppaSubject()) {
			this.prebidConfig.coppa = true;
		}

		this.prebidConfig = {
			...this.prebidConfig,
			...this.configureTargeting(),
			...this.configureTCF(),
			...this.configureS2sBidding(),
			...this.configureJwpRtd(),
			...this.configureOrtb2(),
			...context.get('bidders.prebid.config'),
		};

		this.configureUserSync();
		this.configureSChain();

		this.applyConfig(this.prebidConfig);
		this.configureAdUnits();
		prebidDataTracking.configure(this.adUnits);

		communicationService.on(
			SLOT_REFRESHER_SET_MAXIMUM_SLOT_HEIGHT,
			({ adSlot }) => {
				const slotName = adSlot.getSlotName();
				this.setMaximumAdSlotHeight(slotName);
			},
			false,
		);

		dsaHandler.register();

		utils.logger(logGroup, 'prebid created', this.prebidConfig);
	}

	private configureTargeting(): object {
		if (context.get('bidders.prebid.disableSendAllBids')) {
			return {
				enableSendAllBids: false,
				targetingControls: {
					alwaysIncludeDeals: true,
					allowTargetingKeys: [
						'AD_ID',
						'BIDDER',
						'DEAL',
						'PRICE_BUCKET',
						'SIZE',
						'UUID',
						'CACHE_HOST',
					],
				},
			};
		}

		return {
			enableSendAllBids: true,
			sendBidsControl: {
				bidLimit: 2,
				dealPrioritization: true,
			},
			targetingControls: {
				alwaysIncludeDeals: true,
				allowTargetingKeys: [
					'AD_ID',
					'BIDDER',
					'PRICE_BUCKET',
					'UUID',
					'SIZE',
					'DEAL',
					'CACHE_HOST',
					/* Allows prebid to send back the hb_source key value in bid responses,
					 * which we can send to GAM */
					'SOURCE',
				],
				allowSendAllBidsTargetingKeys: ['AD_ID', 'PRICE_BUCKET', 'UUID', 'SIZE', 'DEAL'],
			},
		};
	}

	private configureUserSync(): void {
		this.configureUID2();
		this.configureTestWikiaUserId();
		this.configureLiveIntentConnectedId();
		this.configureAps();
		this.configureUserSyncIdPriority();

		const apsDisabledIds = context.get('bidders.prebid.aps');

		if (!apsDisabledIds) {
			this.configureOzone();
			this.configureId5();
			this.configureYahooConnectId();
		}
	}

	// only for e2e tests
	private configureTestWikiaUserId() {
		const enabled = !!utils.queryString.get('wikia_adapter');

		if (enabled) {
			this.prebidConfig.userSync.userIds.push({
				name: 'pubCommonId',
				value: {
					pubcid: 'wikiaUserTestId',
				},
			});

			utils.logger(logGroup, 'Wikia user ID enabled');
		}
	}

	private configureLiveIntentConnectedId(): void {
		const liveIntentConnectedIdConfig = connectedId.getConfig();
		if (liveIntentConnectedIdConfig) {
			this.prebidConfig.userSync.userIds.push(liveIntentConnectedIdConfig);
			targetingService.set('li-module-enabled', ['t1']);
			communicationService.emit(eventsRepository.PARTNER_LOAD_STATUS, {
				status: 'liveintent_connectid_started',
			});
			this.updateConnectedIdTargeting();
		} else {
			this.prebidConfig.userSync.userIds.push([]);
			targetingService.set('li-module-enabled', ['t0']);
		}
	}

	private async updateConnectedIdTargeting(): Promise<void> {
		if (connectedId.getConfig()) {
			const eids = await prebidIdRetriever.getIds();
			const isEnrichedWithLI = eids.find((eid) => eid.source === 'liveintent.com');
			targetingService.set('li-module-enabled', [`t1-${isEnrichedWithLI ? 'e1' : 'e0'}`]);
		}
	}

	private configureOzone(): void {
		if (context.get('bidders.prebid.ozone')) {
			this.prebidConfig.userSync.userIds.push({
				name: 'pubCommonId',
				storage: {
					type: 'cookie',
					name: '_pubcid',
					expires: 365,
				},
			});
		}
	}

	private configureId5(): void {
		const id5Config = id5.getConfig();

		if (!id5Config) {
			return;
		}

		this.prebidConfig.userSync.userIds.push(id5Config);

		communicationService.emit(eventsRepository.PARTNER_LOAD_STATUS, {
			status: 'id5_started',
		});
	}

	private configureYahooConnectId(): void {
		const yahooConnectIdConfig = yahooConnectId.getConfig();

		if (!yahooConnectIdConfig) {
			return;
		}

		communicationService.emit(eventsRepository.YAHOO_STARTED);

		this.prebidConfig.userSync.userIds.push(yahooConnectIdConfig);
	}

	private configureUID2(): void {
		if (!uid2.isEnabled()) {
			utils.logger(logGroup, 'UID2 disabled');
			return;
		}

		utils.logger(logGroup, 'UID2 enabled');

		communicationService.on(
			eventsRepository.LIVE_CONNECT_HEM_READY,
			({ hem }) => {
				let uid2Config;

				try {
					uid2Config = uid2.getConfig(hem);
				} catch (error) {
					utils.logger(logGroup, 'UID2 Error', error);
					return;
				}

				if (!uid2Config) {
					return;
				}

				this.prebidConfig.userSync.userIds.push(uid2Config);
				utils.logger(logGroup, 'UID2 configured');

				communicationService.emit(eventsRepository.PARTNER_LOAD_STATUS, {
					status: 'uid2_started',
				});
			},
			true,
		);
	}

	private configureAps(): void {
		const config = aps.getModuleConfig();

		if (!config) {
			return;
		}

		this.prebidConfig.userSync.userIds.push(config);
	}

	private configureUserSyncIdPriority(): void {
		const config = aps.getIdPriorityConfig();

		if (!config) {
			return;
		}

		utils.logger(logGroup, 'IdPriority config', config);

		this.prebidConfig.userSync.idPriority = config;
	}

	private configureSChain(): void {
		this.configureWebAdsSChain();
	}

	private async configureWebAdsSChain(): Promise<void> {
		const pbjs: Pbjs = await pbjsFactory.init();

		pbjs.setBidderConfig({
			bidders: ['relevantdigital'],
			config: {
				schain: {
					validation: 'strict',
					config: {
						ver: '1.0',
						complete: 1,
						nodes: [
							{
								asi: 'http://webads.eu',
								sid: '310035',
								hp: 1,
							},
						],
					},
				},
			},
		});
	}

	private configureTCF(): object {
		const consentConfig = {} as { consentManagement?: object };
		if (utils.getCountryCode() === 'US') {
			consentConfig.consentManagement = {
				usp: {
					cmpApi: 'iab',
					timeout: 100,
				},
			};
		}
		if (this.tcf.exists) {
			consentConfig.consentManagement = {
				...(consentConfig.consentManagement ?? {}),
				gdpr: {
					cmpApi: 'iab',
					timeout: this.timeout,
					allowAuctionWithoutConsent: false,
					defaultGdprScope: false,
				},
			};
		}

		return consentConfig;
	}

	private configureS2sBidding(): object {
		if (!context.get('bidders.s2s.enabled')) {
			return {};
		}

		const s2sBidders = context.get('bidders.s2s.bidders') || [];
		utils.logger(logGroup, 'Prebid s2s enabled', s2sBidders);

		const extPrebidBidders = this.prepareExtPrebidBiders(s2sBidders);

		return {
			cache: {
				url: 'https://prebid-server.rubiconproject.com/cache',
				ignoreBidderCacheKey: true,
			},
			s2sConfig: [
				{
					accountId: s2sRubiconAccountId,
					bidders: s2sBidders,
					defaultVendor: 'rubicon',
					coopSync: true,
					userSyncLimit: 8,
					defaultTTL: 300,
					allowUnknownBidderCodes: true,
					extPrebid: {
						cache: {
							vastxml: { returnCreative: false },
						},
						bidders: extPrebidBidders,
					},
				},
			],
		};
	}

	private configureJwpRtd(): object {
		if (
			context.get('custom.hasFeaturedVideo') &&
			context.get('options.video.enableStrategyRules')
		) {
			const initialMediaId = context.get('options.video.jwplayer.initialMediaId');

			return {
				realTimeData: {
					auctionDelay: 100,
					dataProviders: [
						{
							name: 'jwplayer',
							waitForIt: true,
							params: {
								mediaIDs: initialMediaId ? [initialMediaId] : [],
							},
						},
					],
				},
			};
		}

		return {};
	}

	private configureOrtb2(): object {
		return {
			ortb2: {
				...this.getOrtb2Site(),
				...this.getOrtb2Regs(),
			},
		};
	}

	private getOrtb2Site(): object {
		const orbt2SiteFields = new FandomTaxonomy().getOrtb2IabCategorySiteFields();

		utils.logger(logGroup, 'IAB categories', orbt2SiteFields);

		if (orbt2SiteFields?.cat || orbt2SiteFields?.pagecat) {
			/** @see https://docs.prebid.org/features/firstPartyData.html#supplying-global-data  */
			return {
				site: {
					...orbt2SiteFields,
				},
			};
		}

		return {};
	}

	private getOrtb2Regs(): object {
		if (context.get('options.dsa.enabled')) {
			return {
				regs: {
					ext: {
						dsa: {
							dsarequired: 1,
							pubrender: 2,
							datatopub: 2,
						},
					},
				},
			};
		}

		return {};
	}

	private prepareExtPrebidBiders(s2sBidders: string[]): Record<string, { wrappername: string }> {
		const extPrebidBidders: Record<string, { wrappername: string }> = {};

		s2sBidders.forEach((name) => {
			extPrebidBidders[name] = {
				wrappername: `${s2sRubiconAccountId}_Web_Server`,
			};
		});

		return extPrebidBidders;
	}

	async configureAdUnits(adUnits: PrebidAdUnit[] = []): Promise<void> {
		await pbjsFactory.init();

		if (adUnits.length) {
			this.adUnits = adUnits;
		} else if (!this.adUnits) {
			this.adUnits = adaptersRegistry.setupAdUnits(this.bidGroup);
		}
	}

	async applyConfig(config: Dictionary): Promise<void> {
		const pbjs: Pbjs = await pbjsFactory.init();

		utils.logger(logGroup, 'Apply config');
		return pbjs.setConfig(config);
	}

	async applySettings(): Promise<void> {
		const pbjs: Pbjs = await pbjsFactory.init();

		pbjs.bidderSettings = getSettings();
	}

	protected callBids(bidsBackHandler: (...args: any[]) => void): void {
		if (!this.adUnits) {
			this.adUnits = adaptersRegistry.setupAdUnits(this.bidGroup);
		}

		if (this.adUnits.length === 0) {
			utils.logger(logGroup, 'there is no slots to fetch bids');
			this.onNoSlotsError();
			return;
		}

		this.applySettings();
		this.removeAdUnits();
		this.saveBidIds();
		requestBids(
			this.adUnits,
			() => {
				bidsBackHandler();
				communicationService.emit(BIDDERS_AUCTION_DONE);
			},
			this.timeout,
		);

		communicationService.emit(BIDDERS_BIDS_CALLED);
	}

	private saveBidIds(): void {
		utils.logger(this.logGroup, 'Saving bid ids');
		prebidIdRetriever.saveCurrentPrebidIds();
	}

	async removeAdUnits(): Promise<void> {
		const pbjs: Pbjs = await pbjsFactory.init();

		(pbjs.adUnits || []).forEach((adUnit) => pbjs.removeAdUnit(adUnit.code));
	}

	getTargetingKeys(slotName: string): string[] {
		const allTargetingKeys: string[] = Object.keys(targetingService.dump(slotName) || {});

		return allTargetingKeys.filter((key) => key.indexOf('hb_') === 0);
	}

	getExtendedHbKeyVals(prebidSlotTargeting: PrebidTargeting): Record<string, string> {
		const extendedHbKeyVals: Record<string, string> = {};

		// If the enableSendAllBids prebid config is set to false, then don't generate and send out the extended hb_* values.
		if (context.get('bidders.prebid.disableSendAllBids')) {
			return extendedHbKeyVals;
		}

		const bidderName = prebidSlotTargeting?.['hb_bidder'];
		const bidderPb = prebidSlotTargeting?.['hb_pb'];

		if (bidderName && bidderPb) {
			const bidderAliases = adaptersRegistry.getAdapter(bidderName)?.aliases;
			// Derive all the alias keys, such as for the rubicon-display adapter, which has an alias key of 'rubicon'.
			// Set as undefined, if there are no bidder aliases.
			const bidderAliasKeys = bidderAliases ? Object.keys(bidderAliases) : undefined;

			/* Create a GAM key val that expands upon the hb_bidder key. The key will be appended with the bidderName.
			 * The value will be the bidder name, concatenated by a comma with the alias keys if they exist.
			 *
			 * Keys are limited to 20 characters in GAM, so we cut the key to 20 characters.
			 * Source: https://support.google.com/admanager/answer/1628457?hl=en
			 * */
			extendedHbKeyVals[`hb_bidder_${bidderName}`?.substring(0, 20)] = [bidderName, bidderAliasKeys]
				.filter((val) => !!val)
				.join(',');
			/* Create a GAM key val that expands upon hb_pb key. The key will be appended with the bidderName.
			 * The value will be the same as the hb_pb value.
			 *
			 * Keys are limited to 20 characters in GAM, so we cut the key to 20 characters.
			 * Source: https://support.google.com/admanager/answer/1628457?hl=en
			 * */
			extendedHbKeyVals[`hb_pb_${bidderName}`?.substring(0, 20)] = bidderPb;
		}

		return extendedHbKeyVals;
	}

	async getTargetingParams(slotName: string): Promise<PrebidTargeting> {
		const pbjs: Pbjs = await pbjsFactory.init();
		let targeting: PrebidTargetingForAdUnits;

		try {
			targeting = pbjs.getAdserverTargeting();
		} catch {
			console.warn('Error while getting prebid targeting', slotName);
			targeting = {};
		}

		const prebidSlotTargeting = targeting[getSlotAliasOrName(slotName)] ?? {};
		return {
			...prebidSlotTargeting,
			...this.getExtendedHbKeyVals(prebidSlotTargeting),
		};
	}

	isSupported(slotName: string): boolean {
		const slotAlias: string = getSlotAliasOrName(slotName);

		return this.adUnits && this.adUnits.some((adUnit) => adUnit.code === slotAlias);
	}

	/**
	 * @inheritDoc
	 */
	calculatePrices(): void {
		return;
	}

	setMaximumAdSlotHeight(slotName: string): void {
		const slotRefresherConfig = context.get('slotConfig.slotRefresher.sizes');

		if (!slotRefresherConfig) return;

		const slotHeightLimit = slotRefresherConfig[slotName][1];

		if (!slotHeightLimit) return;

		adaptersRegistry.getAdapters().forEach((adapter) => {
			adapter.setMaximumAdSlotHeight(slotName, slotHeightLimit);
		});
	}
}
